/**
 * Single owner of "the HTTP credentials expired — recover once".
 *
 * This module is deliberately **dependency-free**: it imports nothing. Both
 * transports (`api/client/_shared.ts` fetch and `lib/api-client.ts` Axios) and
 * the proactive EasyAuth scheduler funnel their 401 recovery through it, so it
 * must not pull `auth-api.ts` (top-level `await` at module scope) or
 * `SettingsService` into their import graphs. Everything provider-specific —
 * acquiring credentials, validating identity, publishing the new token,
 * navigating to re-login — is injected by `registerAuthSession()` /
 * `configureAuthRecovery()`.
 *
 * The two counters are the whole design:
 *
 * - **generation** identifies the authenticated session (mode + tenant/object
 *   identity). It only moves when a genuinely different session is registered.
 *   An outcome may never cross a generation boundary: a request dispatched as
 *   user A must not be replayed as user B.
 * - **epoch** identifies the credential inside a generation. It advances on
 *   every successful refresh. A 401 whose request was dispatched at an older
 *   epoch needs no refresh at all — someone else already replaced the
 *   credential, so it can go straight to a replay.
 *
 * There is no wall-clock cooldown and no page-lifetime latch anywhere in here.
 * Recovery is bounded by the counters: one refresh per epoch, one terminal
 * navigation per rejected credential, and both are released by a real session change.
 */

/** Outcome of a provider-level credential re-acquisition. */
export type AuthCredentialRefreshResult =
  | { status: "refreshed" }
  | { status: "session-changed" }
  | { status: "failed"; reason: string }

/** Provider-specific drivers for one authenticated session. */
export interface AuthSessionDriver {
  /**
   * Stable identity of the session: auth mode plus tenant/object identity.
   * Re-registering the same id (React effect reruns, StrictMode double-mounts)
   * refreshes the closures without disturbing in-flight recovery.
   */
  readonly id: string
  /**
   * Re-acquire credentials for this exact session and publish them so both
   * transports pick them up. Must resolve `refreshed` only after the new
   * credential is actually readable through `readCredential()`. Bearer drivers
   * must publish through commitCredential so publication and epoch advancement
   * happen in one synchronous transition, before any queued dispatch can run.
   */
  readonly refresh: (
    isCurrent: () => boolean,
    commitCredential: (publish: () => boolean) => boolean
  ) => Promise<AuthCredentialRefreshResult>
  /** Provider identity/mode must still own this registered session. */
  readonly isCurrent?: () => boolean
  /** The bearer this session currently manages, if it manages one (MSAL). */
  readonly readCredential: () => string | undefined
}

/** Auth state captured when a request is dispatched. */
export interface AuthSnapshot {
  readonly generation: number
  readonly epoch: number
  /** The managed bearer at dispatch time — used to tell ours from a caller's. */
  readonly credential: string | undefined
}

export type AuthRecoveryOutcome =
  /** A refresh ran for this request and produced a new credential. */
  | { status: "refreshed"; epoch: number }
  /** Another request already advanced the credential; replay, don't refresh. */
  | { status: "already-refreshed"; epoch: number }
  /** Recovery is impossible for this credential. Terminal for the epoch. */
  | { status: "failed"; epoch: number; reason: string }
  /** The caller went away while waiting; it must not act on the result. */
  | { status: "cancelled"; epoch: number }
  /** The session changed underneath the request; the outcome does not apply. */
  | { status: "session-changed"; epoch: number }
  /** No authenticated session is registered; nothing to refresh. */
  | { status: "unavailable"; epoch: number }

export interface AuthRecoveryOptions {
  /** Abort signal of the request that hit the 401. */
  signal?: AbortSignal | null
}

interface Flight {
  readonly generation: number
  readonly epoch: number
  credentialCommitted: boolean
  promise: Promise<{ ok: boolean; reason?: string }>
}

let session: AuthSessionDriver | null = null
let sessionInvalidated = false
let navigateToRecovery: ((isCurrent: () => boolean) => void | Promise<void>) | null = null

let generation = 0
const generationListeners = new Set<() => void>()

/** Session-scoped UI caches can subscribe without ever receiving credentials. */
export function subscribeAuthGeneration(listener: () => void): () => void {
  generationListeners.add(listener)
  return () => {
    generationListeners.delete(listener)
  }
}

export function getAuthGeneration(): number {
  return generation
}

function notifyAuthGeneration(): void {
  generationListeners.forEach((listener) => listener())
}
let epoch = 0
let inFlight: Flight | null = null
/** Epoch whose refresh already failed in the current generation, if any. */
let failedEpoch: number | null = null
interface TerminalIncident {
  readonly snapshot: AuthSnapshot
  readonly participants: Set<AbortSignal | null>
}
let terminalIncident: TerminalIncident | null = null

/**
 * Registers the one navigation used when recovery is impossible (forced
 * re-login). Called once by `auth-api.ts`; deduplicated per failed credential by
 * `requestTerminalRecovery()` so concurrent 401s cannot stack redirects.
 */
export function configureAuthRecovery(options: {
  navigate: (isCurrent: () => boolean) => void | Promise<void>
}): void {
  navigateToRecovery = options.navigate
}

/**
 * Registers (or re-registers) the authenticated session. Idempotent for the
 * same `id`: the drivers are swapped in but generation, epoch and any in-flight
 * refresh survive, so a `useAuth` effect rerun cannot cancel a recovery that is
 * already running. A different `id` is a real session change and resets
 * everything, including the terminal-navigation and failed-epoch latches.
 */
export function registerAuthSession(driver: AuthSessionDriver): void {
  sessionInvalidated = false
  if (session && session.id === driver.id) {
    session = driver
    return
  }

  session = driver
  generation += 1
  epoch = 0
  inFlight = null
  failedEpoch = null
  terminalIncident = null
  notifyAuthGeneration()
}

/** The bearer the current session manages, if any. */
export function getManagedCredential(): string | undefined {
  return session?.readCredential()
}

/** Auth state to capture at request dispatch and hand back on a 401. */
export function captureAuthSnapshot(): AuthSnapshot {
  return { generation, epoch, credential: session?.readCredential() }
}

/** The original dispatch still belongs to the active generation and provider. */
export function isAuthSnapshotCurrent(snapshot: AuthSnapshot): boolean {
  return (
    !sessionInvalidated && snapshot.generation === generation && (session?.isCurrent?.() ?? true)
  )
}

export function hasRegisteredAuthSession(): boolean {
  return session !== null
}

/** Terminal side effects belong to the particular credential that failed. */
export function isAuthCredentialCurrent(snapshot: AuthSnapshot): boolean {
  return isAuthSnapshotCurrent(snapshot) && snapshot.epoch === epoch
}

/** Invalidate A's lease after a provider observes another valid principal. */
export function invalidateAuthSession(snapshot: AuthSnapshot): void {
  if (snapshot.generation !== generation) return
  session = null
  sessionInvalidated = true
  generation += 1
  epoch = 0
  inFlight = null
  failedEpoch = null
  terminalIncident = null
  notifyAuthGeneration()
}

/** Join the current failed credential's terminal action while any caller is live. */
export function requestTerminalRecovery(
  snapshot: AuthSnapshot = captureAuthSnapshot(),
  options: AuthRecoveryOptions = {},
  navigate = navigateToRecovery
): boolean {
  if (!isAuthCredentialCurrent(snapshot) || isAborted(options.signal)) return false
  if (
    terminalIncident &&
    terminalIncident.snapshot.generation === snapshot.generation &&
    terminalIncident.snapshot.epoch === snapshot.epoch
  ) {
    terminalIncident.participants.add(options.signal ?? null)
    return false
  }
  const incident: TerminalIncident = {
    snapshot,
    participants: new Set([options.signal ?? null]),
  }
  terminalIncident = incident
  const hasLiveParticipant = () => [...incident.participants].some((signal) => !isAborted(signal))
  const isCurrent = () => isAuthCredentialCurrent(snapshot) && hasLiveParticipant()
  // Followers join before deduplication. Their liveness therefore survives an
  // abort by the caller that happened to start the single backend logout.
  void Promise.resolve(navigate?.(isCurrent)).finally(() => {
    if (!hasLiveParticipant() && terminalIncident === incident) terminalIncident = null
  })
  return true
}

function isAborted(signal: AbortSignal | null | undefined): boolean {
  return signal?.aborted === true
}

const ABORTED = Symbol("aborted")

/** Resolves with the flight's result, or with ABORTED as soon as the caller leaves. */
function waitForFlight<T>(
  promise: Promise<T>,
  signal: AbortSignal | null | undefined
): Promise<T | typeof ABORTED> {
  if (!signal) return promise
  if (signal.aborted) return Promise.resolve(ABORTED)

  return new Promise<T | typeof ABORTED>((resolve) => {
    const onAbort = () => resolve(ABORTED)
    signal.addEventListener("abort", onAbort, { once: true })
    void promise.then(
      (value) => {
        signal.removeEventListener("abort", onAbort)
        resolve(value)
      },
      () => {
        signal.removeEventListener("abort", onAbort)
        resolve(ABORTED)
      }
    )
  })
}

function startFlight(driver: AuthSessionDriver): Flight {
  const flight: Flight = {
    generation,
    epoch,
    credentialCommitted: false,
    promise: Promise.resolve({ ok: false }),
  }

  const isCurrent = () => generation === flight.generation && (driver.isCurrent?.() ?? true)
  const commitCredential = (publish: () => boolean): boolean => {
    if (!isCurrent() || flight.credentialCommitted || epoch !== flight.epoch) return false
    if (!publish()) return false
    // No await separates the externally readable bearer from its epoch.
    epoch += 1
    flight.credentialCommitted = true
    return true
  }
  flight.promise = (async () => {
    try {
      const result = await driver.refresh(isCurrent, commitCredential)

      // The session changed while we were away — this result belongs to nobody.
      if (generation !== flight.generation || !(driver.isCurrent?.() ?? true)) {
        return { ok: false, reason: "session-changed" }
      }

      if (result.status === "session-changed") {
        invalidateAuthSession({
          generation: flight.generation,
          epoch: flight.epoch,
          credential: undefined,
        })
        return { ok: false, reason: "session-changed" }
      }

      if (result.status === "refreshed") {
        if (!flight.credentialCommitted) epoch += 1
        return { ok: true }
      }

      failedEpoch = flight.epoch
      return { ok: false, reason: result.reason }
    } catch (error) {
      if (generation === flight.generation) failedEpoch = flight.epoch
      return {
        ok: false,
        reason: error instanceof Error ? error.message : "refresh-threw",
      }
    } finally {
      if (inFlight === flight) inFlight = null
    }
  })()

  inFlight = flight
  return flight
}

/**
 * Recovers from a 401 observed by a request dispatched at `snapshot`.
 *
 * Concurrent 401s at the same epoch share one refresh. A 401 from an older
 * epoch skips the refresh entirely. A failed epoch is terminal until the
 * session changes: it never re-attempts, and the first *live* caller to see it
 * triggers the (deduplicated) re-login navigation. A caller whose request was
 * aborted gets `cancelled` and can neither replay nor force a logout, while the
 * refresh it was sharing keeps running for the live callers behind it.
 */
export async function recoverFromUnauthorized(
  snapshot: AuthSnapshot,
  options: AuthRecoveryOptions = {}
): Promise<AuthRecoveryOutcome> {
  const { signal } = options

  if (isAborted(signal)) return { status: "cancelled", epoch }
  if (!isAuthSnapshotCurrent(snapshot)) return { status: "session-changed", epoch }
  if (!session) return { status: "unavailable", epoch }
  if (snapshot.epoch < epoch) return { status: "already-refreshed", epoch }

  if (failedEpoch !== null && snapshot.epoch <= failedEpoch) {
    requestTerminalRecovery(snapshot, options)
    return { status: "failed", epoch, reason: "recovery-already-failed" }
  }

  // A bearer may already be committed while its acquisition promise is still
  // unwinding. A rejection of that new epoch cannot join the older flight.
  const flight = inFlight?.epoch === epoch ? inFlight : startFlight(session)
  const settled = await waitForFlight(flight.promise, signal)

  // Cancel points: after the wait, before anything acts on the result.
  if (settled === ABORTED || isAborted(signal)) return { status: "cancelled", epoch }
  if (!isAuthSnapshotCurrent(snapshot)) return { status: "session-changed", epoch }

  if (settled.ok) {
    return {
      status: flight.epoch === snapshot.epoch ? "refreshed" : "already-refreshed",
      epoch,
    }
  }

  requestTerminalRecovery(snapshot, options)
  return { status: "failed", epoch, reason: settled.reason ?? "refresh-failed" }
}

/**
 * Proactive (pre-expiry) refresh. Shares the very same single flight as 401
 * recovery so the scheduler and an in-flight 401 storm cannot both hit the
 * underlying refresh endpoint.
 */
export function refreshCredentialsProactively(): Promise<AuthRecoveryOutcome> {
  return recoverFromUnauthorized(captureAuthSnapshot())
}

/** Test-only: drops all registered drivers and counters. */
export function resetAuthRefreshCoordinatorForTests(): void {
  session = null
  sessionInvalidated = false
  navigateToRecovery = null
  generation = 0
  epoch = 0
  inFlight = null
  failedEpoch = null
  terminalIncident = null
  notifyAuthGeneration()
}
